import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, mergeMap, catchError, tap, switchMap } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { Router } from '@angular/router';
import { ModalService } from '../../core/services/modal.service';
import { ForceChangePasswordModalComponent } from '../../shared/components/modal/force-change-password-modal';
import { ToastService } from '../../core/services/toast.service';
import { UrlService } from '../../core/services/url.service';
import { PushNotificationService } from '../../core/services/push-notification.service';
import * as AuthActions from './auth.actions';
import * as AuthSelectors from './auth.selectors';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private store = inject(Store);
  private router = inject(Router);
  private modalService = inject(ModalService);
  private toastService = inject(ToastService);
  private urlService = inject(UrlService);
  private pushNotificationService = inject(PushNotificationService);

  // Login effect
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({ username, password }) =>
        this.authService.login({ username, password }).pipe(
          mergeMap((response) => {
            // Stocker le token et les données utilisateur
            this.authService.setToken(response.access_token);
            if (response.refresh_token) {
              this.authService.setRefreshToken(response.refresh_token);
            }

            // Stocker le sessionId s'il existe
            if (response.sessionId) {
              this.authService.setSessionId(response.sessionId);
            }

            // Stocker le token de reset_password s'il existe
            if (response.reset_password) {
              this.authService.setResetPasswordToken(response.reset_password);
            }

            // Extraire les données utilisateur du token JWT
            const userData = {
              _id: response.sub,
              email: response.email,
              firstName: response.given_name,
              lastName: response.family_name,
              passportNumber: response.passportNumber,
              phoneNumbers: [
                {
                  countryCode: '221', // Default for Senegal
                  number: response.preferred_username,
                  type: 'mobile',
                  isPrimary: true,
                },
              ],
              birthday: '', // Not available in token
              sex: 'male' as const, // Default
              accountType: 'standard',
              addresses: [],
              nationality: { code: 'SN', label: 'Sénégal' },
            };

            this.authService.setUserData(userData);

            // Si reset_password est défini, déclencher l'action de changement de mot de passe forcé
            if (response.reset_password) {
              return of(
                AuthActions.forcePasswordChange({
                  resetPasswordToken: response.reset_password,
                  user: userData,
                  token: response.access_token,
                })
              );
            }

            // Sinon, retourner le succès de connexion normal
            return of(
              AuthActions.loginSuccess({
                user: userData,
                token: response.access_token,
                refreshToken: response.refresh_token,
              })
            );
          }),
          catchError((error) => {
            let errorMessage = 'Une erreur est survenue lors de la connexion';
            if (error.status === 401) {
              errorMessage = 'Numéro de téléphone ou mot de passe incorrect';
            } else if (error.status === 400) {
              errorMessage = 'Numéro de téléphone ou mot de passe incorrect';
            }
            return of(AuthActions.loginFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Login success effect - ferme le modal, charge les données utilisateur et actualise la page
  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(() => {
        // Fermer le modal de connexion
        this.modalService.closeAll();
        this.router.navigate(this.urlService.buildRouteSegments('passport'));
      }),
      mergeMap(() => [
        AuthActions.loadCurrentUser(), // Charger les données complètes de l'utilisateur
      ])
    )
  );

  // Load current user effect
  loadCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCurrentUser),
      mergeMap(() =>
        this.authService.authenticateUser().pipe(
          map((user) => AuthActions.loadCurrentUserSuccess({ user })),
          catchError((error) => {
            console.error(
              'Erreur lors du chargement des données utilisateur:',
              error
            );
            // Ne pas déconnecter en cas d'erreur, juste retourner l'erreur
            return of(
              AuthActions.loadCurrentUserFailure({
                error: 'Erreur lors du chargement des données utilisateur',
              })
            );
          })
        )
      )
    )
  );

  // Load current user success effect - redirection
  loadCurrentUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadCurrentUserSuccess),
        tap(({ user }) => {
          console.log('✅ Données utilisateur chargées avec succès');

          // Mettre à jour les données utilisateur dans le localStorage
          this.authService.setUserData(user);
          void this.pushNotificationService.identifyUser(user).catch(() => {
            console.warn("Impossible d'initialiser les notifications push");
          });

          // Vérifier si un token de reset_password existe
          const resetPasswordToken = this.authService.getResetPasswordToken();
          console.log('resetPasswordToken', resetPasswordToken);
          if (!resetPasswordToken) {
            // Si pas de token de reset_password, rediriger vers /passport
            // this.router.navigate(['/passport']);
          }
          // Si reset_password existe, le modal de changement de mot de passe sera ouvert par l'effet forcePasswordChange
        })
      ),
    { dispatch: false }
  );

  // Force password change effect
  forcePasswordChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forcePasswordChange),
        tap(({ resetPasswordToken, user, token }) => {
          // Fermer tous les modals existants
          this.modalService.closeAll();

          // Stocker les données utilisateur et le token
          this.authService.setUserData(user);
          this.authService.setToken(token);

          this.store.dispatch(AuthActions.loadCurrentUser());

          // Ouvrir le modal de changement de mot de passe forcé
          this.modalService.open({
            component: ForceChangePasswordModalComponent,
            title: 'Changement de mot de passe requis',
            closable: false, // Empêcher la fermeture
            data: {
              resetPasswordToken: resetPasswordToken,
              user: user,
              token: token,
            },
          });
        }),
        mergeMap(() => [])
      ),
    { dispatch: false }
  );

  // Logout effect
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      mergeMap(() => {
        // Appeler la méthode logout qui gère la requête API et le nettoyage local
        this.authService.logout();
        void this.pushNotificationService.logout().catch(() => {
          console.warn("Impossible de déconnecter les notifications push");
        });
        // Naviguer immédiatement (la requête API est envoyée en arrière-plan)
        this.router.navigate(this.urlService.buildRouteSegments(''));
        return of(AuthActions.logoutSuccess());
      })
    )
  );
}
